import React from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import { CALLOUT_BACKGROUND, CALLOUT_FOREGROUND } from 'lib/form-field-styles'

const useStyles = makeStyles(theme => ({
  tipSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#E0EAFF',
    border: '1px solid #C5D4FF',
    borderRadius: 12,
  },
  tipSectionNeutral: {
    backgroundColor: CALLOUT_BACKGROUND,
    border: '1px solid #E0E0E0',
  },
  tipSectionError: {
    backgroundColor: '#FEECEA',
    border: '1px solid #F5C6C6',
  },
  tipSectionWarning: {
    backgroundColor: '#FEF5E4',
    border: '1px solid #F5DFB8',
  },
  tipSectionWithAction: {
    justifyContent: 'space-between',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  tipCopy: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    minWidth: 0,
    flex: '1 1 auto',
  },
  tipIcon: {
    color: '#0053FF',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tipIconNeutral: {
    color: CALLOUT_FOREGROUND,
  },
  tipIconError: {
    color: '#D32F2F',
  },
  tipIconWarning: {
    color: '#ED6C02',
  },
  tipCaption: {
    fontSize: '0.875rem',
    lineHeight: 1.4,
    color: '#172024',
    margin: 0,
  },
  tipAction: {
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  tipActionButton: {
    borderRadius: 20,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '0.9375rem',
    padding: theme.spacing(1, 2.5),
    whiteSpace: 'nowrap',
    boxShadow: 'none',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 83, 255, 0.28)',
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
}))

export const TipWellActionButton = ({ children, className, ...props }) => {
  const classes = useStyles()

  return (
    <Button
      variant="contained"
      color="primary"
      disableElevation
      className={`${classes.tipActionButton} ${className || ''}`}
      {...props}
    >
      {children}
    </Button>
  )
}

TipWellActionButton.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
}

const TipWell = ({ caption, icon: Icon, action, variant = 'promo' }) => {
  const classes = useStyles()
  const sectionClassName = {
    promo: '',
    neutral: classes.tipSectionNeutral,
    error: classes.tipSectionError,
    warning: classes.tipSectionWarning,
  }[variant]
  const iconClassName = {
    promo: '',
    neutral: classes.tipIconNeutral,
    error: classes.tipIconError,
    warning: classes.tipIconWarning,
  }[variant]

  return (
    <Box
      className={`${classes.tipSection} ${sectionClassName || ''} ${
        action ? classes.tipSectionWithAction : ''
      }`}
    >
      <Box className={classes.tipCopy}>
        {Icon && (
          <Icon
            className={`${classes.tipIcon} ${iconClassName || ''}`}
            fontSize="small"
            opticalSize={20}
          />
        )}
        <Typography component="span" className={classes.tipCaption}>
          {caption}
        </Typography>
      </Box>
      {action ? <Box className={classes.tipAction}>{action}</Box> : null}
    </Box>
  )
}

TipWell.propTypes = {
  caption: PropTypes.node.isRequired,
  icon: PropTypes.elementType,
  action: PropTypes.node,
  variant: PropTypes.oneOf(['promo', 'neutral', 'error', 'warning']),
}

export default TipWell
